import React, { useEffect } from 'react'
import PropTypes from 'prop-types'
import { Controller, FormProvider, useForm } from 'react-hook-form'
import { Box, Grid, makeStyles } from '@material-ui/core'
import { useHistory } from 'react-router-dom'
import { useGetInfographicBlockDetails } from '../hooks/useGetInfographicBlockDetails'
import SydButton from '../../../../../commonDesign/Button'
import SydInput from '../../../../../commonDesign/SydInput'
import SydLabel, { hookFormErrorAdapter } from '../../../../../commonDesign/SydLabel'
import { useSaveInfographicBlockMutation } from '../hooks/useSaveInfographicBlockMutation'
import { ADMIN_ROUTES } from '../../../../../../constants'
import Loading from '../../../../../molecules/Loading'
import { ClientPreviewContextSelect } from '../../components/ClientPreviewContextSelect'
import { PreviewDisplayProvider } from '../../components/PreviewDisplayContext'
import { InfographicBlockDisplay } from './InfographicBlockDisplay'

const useStyles = makeStyles((theme) => ({
  inputsContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2)
  },
  labelDescription: {
    padding: theme.spacing(1),
    marginTop: '-10px',
    fontWeight: theme.typography.weights.light,
    fontSize: theme.typography.fontSizes.md
  }
}))

export const EditInfographicBlockForm = ({
  defaultBlockId,
  onSubmit: _onSubmit
}) => {
  const history = useHistory()
  if (!defaultBlockId) {
    history.replace(ADMIN_ROUTES.INFOGRAPHIC_REPORTS_BLOCK)
  }
  const classes = useStyles()
  const form = useForm({
    mode: 'onChange',
    defaultValues: {
      infographicBlockId: defaultBlockId,
      content: [],
      metadata: {
        size: {
          cols: 12,
          rows: 16
        }
      },
      runtimeConfigValues: {},
      name: '',
      description: '',
      ordinal: 99999
    }
  })

  const infographicBlockId = form.getValues('infographicBlockId')
  const { data, isFetching, refetch } = useGetInfographicBlockDetails({
    infographicBlockId,
    includes: { reportObjects: true }
  }, { enabled: !!infographicBlockId })

  useEffect(() => {
    if (infographicBlockId && data) {
      form.reset({
        ...data,
        content: data.content?.map(section => ({
          ...section,
          size: { cols: 0, rows: 0, ...section.size },
          cords: { x: 0, y: 0, ...section.cords },
          content: section.content?.map((block, index) => ({
            ...block,
            size: { cols: 0, rows: 0, ...block.size },
            cords: { x: 0, y: 0, ...block.cords },
            id: `${block.component}_${index}`
          }))
        })) ?? []
      })
    }
  }, [data, form, infographicBlockId])

  const saveMutation = useSaveInfographicBlockMutation()
  const onSubmit = async (data) => {
    const response = await saveMutation.mutateAsync(data)
    await refetch()
    const parsedBody = JSON.parse(response.body)
    if (parsedBody.infographicBlockId !== infographicBlockId) {
      window.location.push(`${ADMIN_ROUTES.INFOGRAPHIC_REPORTS_BLOCK}/${parsedBody.infographicBlockId}`)
    }
  }

  return (
    <FormProvider {...form}>
      <form {...form} onSubmit={form.handleSubmit(onSubmit)}>
        <Box display='flex' gridGap='8px' flexWrap='wrap'>
          {isFetching ? (
            <Box flex={1} alignItems='center' justifyContent='center'>
              <Loading />
            </Box>
          ) : (
            <>
              <Box flex='1 1' maxWidth='650px' className={classes.inputsContainer}>
                <Controller
                  control={form.control}
                  name='name'
                  rules={{ required: { value: true, message: 'Name required' } }}
                  render={({ field, fieldState }) => (
                    <SydLabel label='Block Name' error={hookFormErrorAdapter(form, fieldState)} required>
                      <SydInput
                        size='sm'
                        {...field}
                      />
                    </SydLabel>
                  )}
                />

                <Controller
                  control={form.control}
                  name='description'
                  render={({ field, fieldState }) => (
                    <SydLabel label='Description' error={hookFormErrorAdapter(form, fieldState)}>
                      <SydInput
                        size='sm'
                        {...field}
                      />
                    </SydLabel>
                  )}
                />

                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <Controller
                      control={form.control}
                      name='metadata.size.rows'
                      rules={{ required: { value: true, message: 'Row height required' } }}
                      render={({ field, fieldState }) => (
                        <SydLabel
                          label='Minimum Height'
                          error={hookFormErrorAdapter(form, fieldState)}
                          required
                          description='Min: 0 / Max: 16)'
                        >
                          <SydInput
                            size='sm'
                            type='number'
                            {...field}
                            inputProps={{
                              increment: 1,
                              min: 1,
                              max: 16
                            }}
                            onChange={(e) => field.onChange({ ...e, target: { value: parseInt(e.target.value) } })}
                          />
                        </SydLabel>
                      )}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <Controller
                      control={form.control}
                      name='metadata.size.cols'
                      rules={{ required: { value: true, message: 'Row height required' } }}
                      render={({ field, fieldState }) => (
                        <SydLabel
                          label='Minimum Width'
                          error={hookFormErrorAdapter(form, fieldState)}
                          required
                          description='Min: 0 / Max: 12)'
                        >
                          <SydInput
                            size='sm'
                            type='number'
                            {...field}
                            inputProps={{
                              increment: 1,
                              min: 1,
                              max: 12
                            }}
                            onChange={(e) => field.onChange({ ...e, target: { value: parseInt(e.target.value) } })}
                          />
                        </SydLabel>
                      )}
                    />
                  </Grid>

                  <p className={classes.labelDescription}>
                    Minimum height and width required for this report block.<br />
                    It can be expanded larger than this in a template, but will not fit in smaller items<br />
                  </p>

                  <div className='w-full flex justify-between mt-4'>
                    <SydButton
                      variant='secondary'
                      type='button'
                      onClick={() => history.replace(ADMIN_ROUTES.INFOGRAPHIC_REPORTS_BLOCK)}
                      disabled={saveMutation.isLoading}
                    >
                      Cancel
                    </SydButton>
                    <SydButton
                      variant='primary'
                      type='submit'
                      processing={saveMutation.isLoading}
                      disabled={saveMutation.isLoading}
                      onClick={form.handleSubmit(onSubmit)}
                    >
                      {saveMutation.isLoading ? 'Loading...' : 'Save'}
                    </SydButton>
                  </div>
                </Grid>
              </Box>
              <Box flex='1 0'>
                <ClientPreviewContextSelect />

                <PreviewDisplayProvider editingLevel='block'>
                  <InfographicBlockDisplay />
                </PreviewDisplayProvider>
              </Box>
            </>
          )}
        </Box>
      </form>
    </FormProvider>
  )
}

EditInfographicBlockForm.propTypes = {
  defaultBlockId: PropTypes.number,
  onSubmit: PropTypes.func
}
